import React, { createContext, useContext, useState, useEffect } from "react"
import { Product, ProductResponseDto } from "@/api/api-client"

export interface CartItem extends ProductResponseDto {
  quantity: number
  pickupWarehouseId?: number
}

interface CartContextType {
  items: CartItem[]
  addItem: (product: ProductResponseDto, quantity?: number, pickupWarehouseId?: number) => void
  removeItem: (productId: number) => void
  updateQuantity: (productId: number, quantity: number) => void
  setPickupWarehouse: (productId: number, warehouseId: number | undefined) => void
  clearCart: () => void
  totalItems: number
  totalPrice: number
  isCartOpen: boolean
  setIsCartOpen: (open: boolean) => void
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error("useCart must be used within a CartProvider")
  }
  return context
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    const savedCart = localStorage.getItem("meatbid_cart")
    return savedCart ? JSON.parse(savedCart) : []
  })
  const [isCartOpen, setIsCartOpen] = useState(false)

  useEffect(() => {
    localStorage.setItem("meatbid_cart", JSON.stringify(items))
  }, [items])

  const addItem = (product: ProductResponseDto, quantity = 1, pickupWarehouseId?: number) => {
    const quantityToAdd = Number.isFinite(quantity) && quantity > 0 ? quantity : 1

    setItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id)
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id
            ? {
                ...item,
                quantity: item.quantity + quantityToAdd,
                pickupWarehouseId: pickupWarehouseId ?? item.pickupWarehouseId,
              }
            : item
        )
      }
      return [...prevItems, { ...product, quantity: quantityToAdd, pickupWarehouseId }]
    })
    // Open the cart automatically
    setIsCartOpen(true)
  }

  const removeItem = (productId: number) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== productId))
  }

  const updateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productId)
      return
    }
    setItems((prevItems) =>
      prevItems.map((item) => (item.id === productId ? { ...item, quantity } : item))
    )
  }

  const setPickupWarehouse = (productId: number, warehouseId: number | undefined) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === productId ? { ...item, pickupWarehouseId: warehouseId } : item
      )
    )
  }

  const clearCart = () => {
    setItems([])
  }

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)
  const totalPrice = items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0)

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        setPickupWarehouse,
        clearCart,
        totalItems,
        totalPrice,
        isCartOpen,
        setIsCartOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

